import { v4 as uuidv4 } from 'uuid';
import indexedDBService, { putItem, getItemById, getAllItems } from './indexedDBService';

// Define notification types
export type NotificationPriority = 'high' | 'medium' | 'low';
export type NotificationStatus = 'overdue' | 'due' | 'upcoming' | 'completed';

export interface DueDate {
  id: string;
  title: string;
  dueDate: Date;
  type: 'tax_filing' | 'payment' | 'receivable' | 'payable' | 'inventory' | 'overdue_payment';
  description: string;
  amount?: number;
  status: NotificationStatus;
  priority: NotificationPriority;
  completed?: boolean;
  entityId?: string; // Reference to a client, vendor, or inventory item
  userId?: string; // User this notification belongs to
  timestamp?: any;
  completedAt?: any;
  updatedAt?: any;
}

// Get static due dates and ensure they're in the database
const syncStaticDueDates = async (userId: string): Promise<DueDate[]> => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const staticDueDates: DueDate[] = [];

  // Helper to add date
  const addDate = (idPrefix: string, title: string, date: Date, type: DueDate['type'], desc: string) => {
    staticDueDates.push({
      id: `${idPrefix}-${date.getTime()}`,
      title,
      dueDate: date,
      type,
      description: desc,
      status: determineDueStatus(date),
      priority: determinePriority(date),
      userId: userId
    });
  };

  // GSTR-1
  const gstr1DueDate = new Date(currentYear, currentMonth, 11);
  if (gstr1DueDate < today) gstr1DueDate.setMonth(gstr1DueDate.getMonth() + 1);
  addDate('gstr1', 'GSTR-1 Filing', gstr1DueDate, 'tax_filing', 'File GSTR-1 return for the previous month');

  // GSTR-3B
  const gstr3bDueDate = new Date(currentYear, currentMonth, 20);
  if (gstr3bDueDate < today) gstr3bDueDate.setMonth(gstr3bDueDate.getMonth() + 1);
  addDate('gstr3b', 'GSTR-3B Filing', gstr3bDueDate, 'tax_filing', 'File GSTR-3B return for the previous month');

  // GSTR-9
  const gstr9DueDate = new Date(currentYear, 11, 31);
  addDate('gstr9', 'GSTR-9 Annual Filing', gstr9DueDate, 'tax_filing', `File GSTR-9 annual return for FY ${currentYear - 1}-${currentYear}`);

  // TDS Payment
  const tdsDueDate = new Date(currentYear, currentMonth, 7);
  if (tdsDueDate < today) tdsDueDate.setMonth(tdsDueDate.getMonth() + 1);
  addDate('tds-payment', 'TDS Payment', tdsDueDate, 'payment', 'Pay TDS for the previous month');

  // TDS Filings
  const tdsFilings = [
    { date: new Date(currentYear, 6, 31), quarter: 'Q1' },
    { date: new Date(currentYear, 9, 31), quarter: 'Q2' },
    { date: new Date(currentYear, 0, 31), quarter: 'Q3' },
    { date: new Date(currentYear, 4, 31), quarter: 'Q4' },
  ];
  tdsFilings.forEach(({ date, quarter }) => {
    if (date > today || date.getTime() - today.getTime() < 30 * 24 * 60 * 60 * 1000) {
      addDate('tds-filing', 'TDS Filing', date, 'tax_filing', `File TDS return for ${quarter}`);
    }
  });

  // PF Payment
  const pfDueDate = new Date(currentYear, currentMonth, 15);
  if (pfDueDate < today) pfDueDate.setMonth(pfDueDate.getMonth() + 1);
  addDate('pf-payment', 'PF Payment & Filing', pfDueDate, 'payment', 'Pay and file PF for the previous month');

  // ITR
  const itrDueDate = new Date(currentYear, 6, 31);
  if (itrDueDate > today || itrDueDate.getTime() - today.getTime() < 60 * 24 * 60 * 60 * 1000) {
    addDate('itr', 'Income Tax Return Filing', itrDueDate, 'tax_filing', `File Income Tax Return for AY ${currentYear}-${currentYear + 1}`);
  }

  // Advance Tax
  const advanceTaxDates = [
    { date: new Date(currentYear, 5, 15), quarter: 'Q1' },
    { date: new Date(currentYear, 8, 15), quarter: 'Q2' },
    { date: new Date(currentYear, 11, 15), quarter: 'Q3' },
    { date: new Date(currentYear, 2, 15), quarter: 'Q4' },
  ];
  advanceTaxDates.forEach(({ date, quarter }) => {
    if (date > today || date.getTime() - today.getTime() < 30 * 24 * 60 * 60 * 1000) {
      addDate('advance-tax', 'Advance Tax Payment', date, 'payment', `Pay Advance Tax for ${quarter}`);
    }
  });

  // Store in IndexedDB
  await Promise.all(staticDueDates.map(async (notification) => {
    const existingNotif = await getItemById<DueDate>('notifications', notification.id);

    if (!existingNotif) {
      // Add new
      await putItem('notifications', {
        ...notification,
        timestamp: new Date().toISOString()
      });
    } else {
      // Update existing if status changed and not completed
      if (existingNotif.completed) {
        notification.completed = true;
        notification.status = 'completed';
      } else if (notification.status !== existingNotif.status) {
        await putItem('notifications', {
          ...existingNotif,
          status: notification.status,
          priority: notification.priority
        });
      }
    }
  }));

  return staticDueDates;
};

// Get overdue payments
export const getOverduePayments = async (userId: string): Promise<any[]> => {
  try {
    const overdueVendors: any[] = [];
    const overdueClients: any[] = [];

    // Get vendors
    const vendors = await getAllItems<any>('vendors');
    vendors.forEach(vendor => {
      if (vendor.pendingPayments && vendor.pendingPayments > 0) {
        overdueVendors.push({
          id: vendor.id,
          type: 'payable',
          dueDate: vendor.paymentDueDate || new Date().toISOString(),
          name: vendor.name,
          amount: vendor.pendingPayments
        });
      }
    });

    // Get clients
    const clients = await getAllItems<any>('clients');
    clients.forEach(client => {
      if (client.pendingAmount && client.pendingAmount > 0) {
        overdueClients.push({
          id: client.id,
          type: 'receivable',
          dueDate: client.dueDate || new Date().toISOString(),
          name: client.name,
          amount: client.pendingAmount
        });
      }
    });

    return [...overdueVendors, ...overdueClients];
  } catch (error) {
    console.error('Error fetching overdue payments:', error);
    return [];
  }
};

// Get pending inventory
export const getPendingInventory = async (userId: string): Promise<any[]> => {
  try {
    const inventory = await getAllItems<any>('inventory');
    const pendingItems: any[] = [];

    inventory.forEach(item => {
      if (item.inTransit) {
        pendingItems.push({
          id: item.id,
          type: 'inventory',
          dueDate: item.expectedDelivery || new Date().toISOString(),
          name: item.name,
          description: `${item.name} in transit, expected delivery on ${new Date(item.expectedDelivery || '').toLocaleDateString()}`
        });
      }
      if (item.stock < item.reorderLevel) {
        pendingItems.push({
          id: item.id,
          type: 'inventory',
          dueDate: new Date().toISOString(),
          name: item.name,
          description: `${item.name} low in stock (${item.stock} units), reorder level: ${item.reorderLevel}`
        });
      }
    });
    return pendingItems;
  } catch (error) {
    console.error('Error fetching pending inventory:', error);
    return [];
  }
};

function determineDueStatus(dueDate: Date): NotificationStatus {
  const today = new Date();
  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'overdue';
  else if (diffDays <= 3) return 'due';
  else return 'upcoming';
}

function determinePriority(dueDate: Date): NotificationPriority {
  const status = determineDueStatus(dueDate);
  if (status === 'overdue') return 'high';
  else if (status === 'due') return 'medium';
  else return 'low';
}

// Get completed notifications
export const getCompletedNotifications = async (userId: string): Promise<DueDate[]> => {
  try {
    const notifications = await getAllItems<DueDate>('notifications');
    const completed = notifications
      .filter(n => n.completed)
      .sort((a, b) => new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()); // Sort desc

    // Fix date objects
    completed.forEach(n => {
      n.dueDate = new Date(n.dueDate);
    });

    return completed;
  } catch (error) {
    console.error('Error fetching completed notifications:', error);
    return [];
  }
};

// Main function to get all due dates
export const getDueDates = async (userId: string): Promise<DueDate[]> => {
  if (!userId) return [];

  try {
    await syncStaticDueDates(userId);

    const notifications = await getAllItems<DueDate>('notifications');
    const relevant = notifications.filter(n => {
      return n.status === 'overdue' || n.status === 'due' || n.completed;
    }).sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()); // asc

    relevant.forEach(n => n.dueDate = new Date(n.dueDate));

    return relevant;
  } catch (error) {
    console.error('Error getting due dates:', error);
    return [];
  }
};

// Update status
export const updateNotificationStatus = async (
  id: string,
  status: 'completed' | 'pending',
  nextDate?: Date
): Promise<boolean> => {
  try {
    const notif = await getItemById<DueDate>('notifications', id);
    if (!notif) return false;

    if (status === 'completed') {
      await putItem('notifications', {
        ...notif,
        completed: true,
        status: 'completed',
        completedAt: new Date().toISOString()
      });
    } else if (status === 'pending' && nextDate) {
      await putItem('notifications', {
        ...notif,
        dueDate: nextDate,
        status: determineDueStatus(nextDate),
        priority: determinePriority(nextDate),
        updatedAt: new Date().toISOString()
      });
    }

    return true;
  } catch (error) {
    console.error('Error updating notification status:', error);
    return false;
  }
};
